import { useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';
import { useSenzaRete } from '../../hooks/useSenzaRete';
import './AvvisoSenzaRete.css';

interface AvvisoSenzaReteProps {
  /**
   * Cosa aggiungere sotto al titolo.
   *
   * `'home'` dice cosa si puo' ancora fare — ed e' l'unico posto in cui abbia
   * senso dirlo, perche' e' l'unico in cui i pulsanti che lo realizzano (sfida
   * al computer, partita in presenza) sono a schermo mentre si legge.
   * `'dati'` dice perche' la pagina e' vuota.
   */
  contesto?: 'home' | 'dati';
}

/**
 * «Sei senza connessione» — in cima alla schermata, non al posto della schermata.
 *
 * Prima di questo avviso l'app, aperta senza rete, non nominava MAI la causa: i
 * problemi dicevano «non e' stato possibile avviare il problema», il profilo
 * mostrava «giocatore non trovato», la libreria dell'editor una riga di errore
 * col salvataggio spento e nessuna spiegazione, e i problemi del giorno restavano
 * a scheletro per sempre. Quattro guasti diversi a schermo per un fatto solo, e
 * quel fatto il browser lo sapeva gia'.
 *
 * Non si richiude: sparisce da sola quando la rete torna. Un pulsante di chiusura
 * vorrebbe dire tenere uno stato «l'ho gia' letto» e decidere quando riarmarlo —
 * lavoro vero per togliere una riga che dura quanto il problema che descrive.
 */
export default function AvvisoSenzaRete({ contesto = 'dati' }: AvvisoSenzaReteProps) {
  const { t } = useTranslation();
  const senzaRete = useSenzaRete();

  if (!senzaRete) return null;

  return (
    <div className="avviso-senza-rete" role="status" data-testid="avviso-senza-rete">
      <span className="asr-icona" aria-hidden="true"><WifiOff size={18} /></span>
      <span className="asr-testo">
        <span className="asr-titolo">{t('senzaRete.titolo', 'You are offline')}</span>
        <span className="asr-corpo">
          {contesto === 'home'
            ? t(
                'senzaRete.home',
                'Online games, tournaments and the leaderboard are unavailable. You can still play against the computer or over the board on this device.'
              )
            : t('senzaRete.dati', "This page can't load its data until the connection is back.")}
        </span>
      </span>
    </div>
  );
}
